if (!customElements.get('education-swiper')) {
  class EducationSwiper extends HTMLElement {
    swiper = null;
    resizeTimeout = null;
    static MOBILE_BREAKPOINT = 1024;

    connectedCallback() {
      this.init();

      this._onLabelClick = (e) => {
        const label = e.target.closest('.lp-accordion__header');
        if (!label) return;
        const radio = this.querySelector(`#${label.htmlFor}`);
        if (radio && radio.checked) {
          e.preventDefault();
          radio.checked = false;
        }
      };
      this.addEventListener('click', this._onLabelClick);

      this.onResize = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.init(), 150);
      };
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onLabelClick);
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.resizeTimeout);
      clearTimeout(this.swiperRetry);
      this.destroySwiper();
    }

    init() {
      const isMobile = window.innerWidth < EducationSwiper.MOBILE_BREAKPOINT;
      const enableMobile = this.dataset.enableMobile === 'true';

      if (isMobile && enableMobile) {
        this.createSwiper();
      } else {
        this.destroySwiper();
      }
    }

    createSwiper() {
      if (this.swiper) {
        return;
      }

      if (typeof Swiper === 'undefined') {
        this.swiperRetry = setTimeout(() => this.createSwiper(), 100);
        return;
      }

      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) {
        return;
      }

      const showPagination = this.dataset.showPagination === 'true';
      const paginationEl = swiperEl.querySelector('.swiper-pagination');

      const config = {
        slidesPerView: 1,
        spaceBetween: 16,
        watchOverflow: true,
      };

      if (showPagination && paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
        };
      }

      this.swiper = new Swiper(swiperEl, config);
      this.classList.add('is-swiper-active');
    }

    destroySwiper() {
      clearTimeout(this.swiperRetry);

      if (!this.swiper) {
        this.classList.remove('is-swiper-active');
        return;
      }

      this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.remove('is-swiper-active');
    }
  }

  customElements.define('education-swiper', EducationSwiper);
}

if (!customElements.get('education-video')) {
  customElements.define('education-video',
    class EducationVideo extends HTMLElement {
      connectedCallback() {
        this._video = this.querySelector('video');
        if (!this._video) return;

        this._observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (this._video.readyState === 0) this._video.load();
              this._video.play().catch(() => {});
            } else {
              this._video.pause();
            }
          }
        }, { rootMargin: '250px', threshold: 0.1 });

        this._observer.observe(this._video);
      }

      disconnectedCallback() {
        if (this._observer) this._observer.disconnect();
        if (this._video) this._video.pause();
      }
    }
  );
}

if (!customElements.get('education-progress')) {
  customElements.define('education-progress',
    class EducationProgress extends HTMLElement {
      progress = null;
      ticking = false;
      resizeTimeout = null;
      onScroll = () => this.requestUpdate();
      onResize = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => this.requestUpdate(), 150);
      };

      connectedCallback() {
        this.progress = this.querySelector('.lp-progress');
        if (!this.progress) return;

        const fill = parseFloat(this.dataset.fill) || 75;
        this.progress.style.setProperty('--progress-target', fill + '%');

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);
        this.update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
        clearTimeout(this.resizeTimeout);
        this.progress = null;
      }

      requestUpdate() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.update();
          this.ticking = false;
        });
      }

      update() {
        if (!this.progress) return;

        const { top } = this.progress.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        const isDesktop = window.innerWidth >= 1024;
        const headerEl = isDesktop ? document.querySelector('#main-header') : null;
        const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 0;

        const effectiveHeight = viewportHeight - headerHeight;
        const adjustedTop = top - headerHeight;

        const center = effectiveHeight / 2;
        const holdUntil = effectiveHeight * 0.15;

        let fraction;
        if (adjustedTop > center) {
          fraction = (effectiveHeight - adjustedTop) / center;
        } else if (adjustedTop > holdUntil) {
          fraction = 1;
        } else {
          fraction = adjustedTop / holdUntil;
        }

        fraction = Math.min(1, Math.max(0, fraction));

        this.progress.style.setProperty('--lp-progress', fraction.toFixed(4));
        this.progress.classList.toggle('is-complete', fraction >= 0.95);
      }
    }
  );
}
